import { Add, Delete } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import React from "react";
import { useFieldArray } from "react-hook-form";
import FormTextField from "../../Common/FormTextField";
import ConfigExtractionQueryDialog from "../../useDialogs/ConfigExtractionQueryTypeDialog";
import { useDialogs } from "../../useDialogs/useDialogs";
import {
  defaultAggregateExtractionQuery,
  defaultComputedExtractionQuery,
  defaultExtractionRoutedQuery,
  defaultLLMExtractionQuery,
  defaultPredefinedExtractionQuery,
  defaultRegexExtractionQuery,
} from "./ConfigDefault";
import ConfigExtractionQuery from "./ConfigExtractionQuery";
import ConfigExtractionRoutedQuery from "./ConfigExtractionRoutedQuery";
import { useFormConfigContext } from "./ConfigFormContext";

const ConfigExtraction: React.FC = () => {
  const { control, setValue, watch } = useFormConfigContext();

  const {
    fields: extractionQueries,
    append: addQuery,
    remove: removeQuery,
  } = useFieldArray({
    control,
    name: "config.extraction.queries",
  });

  const addRoutedQuery = () => addQuery(defaultExtractionRoutedQuery());
  const addLLMQuery = () => addQuery(defaultLLMExtractionQuery());
  const addRegexQuery = () => addQuery(defaultRegexExtractionQuery());
  const addPredefinedQuery = () => addQuery(defaultPredefinedExtractionQuery());
  const addComputedQuery = () => addQuery(defaultComputedExtractionQuery());
  const addAggregateQuery = () => addQuery(defaultAggregateExtractionQuery());

  const dialogs = useDialogs();

  const split = watch("config.split");
  const classification = watch("config.classification");

  let routes =
    split?.segmentation.classes?.map((classe) => `split.${classe.name}`) || [];
  split?.segmentation.default && routes.push(split?.segmentation.default.name);
  if (classification) {
    classification.queries.forEach((query) => {
      if ("queries" in query) {
        query.queries.forEach((q) => {
          q.classes.forEach((classe) =>
            routes.push(`classification.${q.name}.${classe.name}`)
          );
          q.default &&
            routes.push(`classification.${q.name}.${q.default.name}`);
        });
      } else {
        query.classes.forEach((classe) =>
          routes.push(`classification.${query.name}.${classe.name}`)
        );
        query.default &&
          routes.push(`classification.${query.name}.${query.default.name}`);
      }
    });
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <Typography className="text-xl font-semibold">Extraction</Typography>
        <div className="flex flex-row gap-2">
          <Button
            variant="contained"
            className="gap-2"
            onClick={() =>
              dialogs.open(ConfigExtractionQueryDialog, {
                addLLMQuery: addLLMQuery,
                addRegexQuery: addRegexQuery,
                addPredefinedQuery: addPredefinedQuery,
                addComputedQuery: addComputedQuery,
                addAggregateQuery: addAggregateQuery,
              })
            }
          >
            <Add />
            Extraction
          </Button>
          <Button
            variant="contained"
            className="gap-2"
            onClick={addRoutedQuery}
            disabled={routes.length < 1}
          >
            <Add />
            Groupe
          </Button>
          <Button
            color="error"
            onClick={() => setValue("config.extraction", undefined)}
          >
            <Delete />
          </Button>
        </div>
      </div>
      <Stack rowGap={2} className="py-4 px-4 overflow-y-scroll">
        <FormTextField
          size="small"
          multiline
          control={control}
          name={`config.extraction.params.context_description`}
          label="Description des documents reçus"
          placeholder="Description des documents reçus (ex: Les documents suivants sont fournis par un client pour adhérer à notre service)"
        />
        {extractionQueries?.map((query, index) => {
          if (!query.hasOwnProperty("queries")) {
            return (
              <ConfigExtractionQuery
                key={query.id}
                index={index}
                removeQuery={() => removeQuery(index)}
              />
            );
          } else {
            return (
              <ConfigExtractionRoutedQuery
                key={query.id}
                index={index}
                removeRouted={() => removeQuery(index)}
                routes={routes}
              />
            );
          }
        })}
      </Stack>
    </>
  );
};
export default ConfigExtraction;
